import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { AlertTriangle } from "lucide-react";
import { ContextualHelp } from "@shared/ui/contextual-help";
import { ScrollArea } from "@shared/ui/scroll-area";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import {
  useAvabilitySettings,
  useNotificationTemplate,
  usePriorityLevel,
  useServiceCategoryByUtility,
  useServiceSubCategoryByCategory,
  useUpdateServiceConfig,
  useUtilityServices,
} from "@features/cx/settings/hooks";
import { useCreateServiceConfig } from "@features/cx/settings/hooks";
import { toast } from "@shared/hooks/use-toast";
import { useBaseComponent } from "@shared/base/hooks/useBaseComponent";
import { ServiceDetailFormProps } from "@features/cx/settings/types";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

export function ServiceDetailForm({
  service,
  open,
  onClose,
  onSave,
}: ServiceDetailFormProps) {
  const remoteUtilityId = useRemoteUtilityId();
  const [formData, setFormData] = useState({
    name: service?.name,
    code: service?.code,
    description: service?.description,
    utility_service: service?.productCode,
    category: service?.supportRequestType,
    subcategory: service?.supportRequestSubtype,
    priority: service?.extraData?.priorityLevel,
    responseTime: service?.extraData?.responseTime,
    acknowledgmentTime: service?.extraData.acknowlegmentTime || "",
    resolutionTime: service?.extraData?.resolutionTime,
    serviceFee: service?.extraData?.serviceCharge,
    fieldWorkRequired: service?.extraData?.fieldForceRequired,
    customerNotification: service?.extraData?.consumerNotification,
    businessHours: service?.extraData?.businessHours,
    availability: service?.extraData?.availability,
    requiresApproval: service?.extraData?.requiresApproval,
    visibleOnSelfServicePortal:
      service?.extraData?.is_visible_on_self_portal === 1 ||
      service?.extraData?.is_visible_on_self_portal === true ||
      service?.extraData?.visibleOnSelfServicePortal ||
      false,
  });

  // Mock categories check
  const categoriesExist = true;
  const subcategoriesExist = true;

  // Data hooks
  const { data: utilities } = useUtilityServices();
  const { data: priorityData } = usePriorityLevel();
  const { data: templateData } = useNotificationTemplate();
  const { data: availabilityData } = useAvabilitySettings();

  // State for dependent dropdowns
  const [utilityService, setUtilityService] = useState<any>("");
  const [codeList, setCodeList] = useState<any>("");
  const [selectedUtility, setSelectedUtility] = useState(
    service?.productCode || "",
  );

  // Dependent data hooks
  const {
    data: serviceCategories = [],
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useServiceCategoryByUtility(utilityService);
  const { data: subServiceCategories = [], refetch: refetchSubCategories } =
    useServiceSubCategoryByCategory(codeList);

  // Hook for creating service config
  const createServiceConfig = useCreateServiceConfig();
  const updateConfig = useUpdateServiceConfig(service?.id);
  useEffect(() => {
    if (open) {
      refetchCategories();
      refetchSubCategories();
    }
  }, [open, refetchCategories, refetchSubCategories]);

  useEffect(() => {
    const operation = service ? "EDIT" : "CREATE";

    switch (operation) {
      case "EDIT":
        setFormData({
          name: service.name || "",
          code: service.configurationCode || "",
          description: service.longDescription || "",
          utility_service: service.productCode || [],
          category: service.supportRequestType || "",
          subcategory: service.supportRequestSubtype || "",
          priority: service.extraData?.priorityLevel?.toString() || "",
          responseTime: service.extraData?.responseTime || "",
          acknowledgmentTime: service.extraData?.acknowlegmentTime || "",
          resolutionTime: service.extraData?.resolutionTime || "",
          serviceFee: service.extraData?.serviceCharge || "",
          fieldWorkRequired: service.extraData?.fieldForceRequired || false,
          customerNotification:
            service.extraData?.consumerNotification?.toString() || "",
          businessHours: service.extraData?.businessHours || "",
          availability: service.extraData?.availability?.toString() || "",
          requiresApproval: service.extraData?.requiresApproval || false,
          visibleOnSelfServicePortal:
            service.extraData?.is_visible_on_self_portal === 1 ||
            service.extraData?.is_visible_on_self_portal === true ||
            service.extraData?.visibleOnSelfServicePortal ||
            false,
        });
        setSelectedUtility(service.productCode || "");
        // Also set the dependent dropdown states for edit mode
        setUtilityService(service.productCode || "");
        setCodeList(service.supportRequestType || "");
        break;
    }
  }, [service]);

  // Memoized options
  const getUtilityOptions = useMemo(() => {
    if (!utilities || !Array.isArray(utilities)) {
      return [];
    }
    return utilities
      .filter((utility) => utility.isActive === true)
      .map((utility) => ({
        value: utility.name,
        label: utility.name,
      }));
  }, [utilities]);

  const getPriorityOptions = useMemo(() => {
    if (!priorityData || !Array.isArray(priorityData)) {
      return [];
    }
    return priorityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [priorityData]);

  const getTemplateOptions = useMemo(() => {
    if (!templateData || !Array.isArray(templateData)) {
      return [];
    }
    return templateData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [templateData]);

  const getAvabilibiltyOptions = useMemo(() => {
    if (!availabilityData || !Array.isArray(availabilityData)) {
      return [];
    }
    return availabilityData.map((data) => ({
      value: data.key,
      label: data.value,
    }));
  }, [availabilityData]);

  const getServiceCategoryOptions = useMemo(() => {
    if (!serviceCategories || !Array.isArray(serviceCategories)) {
      return [];
    }
    return serviceCategories.map((category) => {
      return {
        value: category.code,
        label: category.name,
      };
    });
  }, [serviceCategories]);

  const getSubServiceCategoryOptions = useMemo(() => {
    if (!subServiceCategories || !Array.isArray(subServiceCategories)) {
      return [];
    }
    return subServiceCategories.map((subCategory) => {
      return {
        value: subCategory.code,
        label: subCategory.name,
      };
    });
  }, [subServiceCategories]);

  const formFields: FormField[] = [
    // Basic Service Information Group
    {
      name: "name",
      type: "text",
      label: "Service Name",
      placeholder: "e.g., New Water Connection",
      required: true,
      disabled: !!service,
      group: "Basic Service Information",
      groupColorNeeded: false,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Enter a clear, descriptive name for this service. This name will appear in menus, reports, and customer-facing materials. Keep it concise but specific.",
    },
    {
      name: "code",
      type: "text",
      label: "Service Code",
      placeholder: "e.g., WC-NEW-001",
      disabled: !!service,
      required: true,
      group: "Basic Service Information",
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "A unique identifier for this service used in tracking, reporting, and integration with other systems. Can be auto-generated or manually entered following your organization's coding standards.",
    },
    {
      name: "description",
      type: "textarea",
      label: "Service Description",
      placeholder:
        "Detailed service description including process, requirements, and outcomes",
      rows: 3,
      group: "Basic Service Information",
      className: "col-span-2",
      showHelperTooltip: true,
      tooltipText:
        "Provide a detailed description of what this service includes, any prerequisites, and what customers can expect. This helps staff understand the service scope and assists customers.",
    },

    // Service Classification Group
    {
      name: "utility_service",
      label: "Utility Services",
      type: "multi-select",
      required: true,
      options: getUtilityOptions,
      placeholder: "Select utility services...",
      fullWidth: true,
      searchable: true,
      groupColorNeeded: false,
      clearable: true,
      maxDisplayItems: 3,
      closeOnSelect: false,
      group: "Service Classification",
      className: "col-span-2",
      classes: {
        container: "w-full mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: `w-full border-2 border-blue-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200`,
        error: "text-red-600 text-sm mt-1",
      },
      showHelperTooltip: true,
      tooltipText:
        "Select the specific utility service (e.g., Water, Sewer, Solid Waste) under which this service category will be classified. This ensures services are routed to the correct department for resolution.",
    },
    {
      name: "category",
      type: "select",
      label: "Service Category",
      placeholder: "Select category",
      required: true,
      group: "Service Classification",
      options: getServiceCategoryOptions,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Main category that groups similar services together. This affects default settings, reporting, and how the service appears in navigation menus.",
    },
    {
      name: "subcategory",
      type: "select",
      label: "Service Subcategory",
      placeholder: "Select subcategory",
      required: true,
      group: "Service Classification",
      options: getSubServiceCategoryOptions,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Main category that groups similar services together. This affects default settings, reporting, and how the service appears in navigation menus.",
    },

    // Service Configuration Group
    {
      name: "priority",
      type: "select",
      label: "Priority Level",
      placeholder: "Select priority",
      required: true,
      group: "Service Configuration",
      groupColorNeeded: false,
      options: getPriorityOptions,
      className: "col-span-1",
      showHelperTooltip: true,
      tooltipText:
        "Determines the urgency and processing order of service requests. Higher priority services get faster response times and escalation procedures.",
    },
    {
      name: "serviceFee",
      type: "number",
      label: "Service Fee",
      placeholder: "Enter fee amount",
      group: "Service Configuration",
      className: "col-span-1",
      min: 0,
      showHelperTooltip: true,
      tooltipText:
        "Cost charged to customers for this service, if applicable. Leave empty for free services. This amount will be used for billing and quotations.",
    },
    {
      name: "acknowledgmentTime",
      type: "number",
      label: "Acknowledgment Time (hours)",
      placeholder: "Enter Acknowledgment Time",
      group: "Service Configuration",
      className: "col-span-1",
      min: 0,
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to acknowledge receipt of the complaint and send initial response to the customer. This is the first customer touchpoint.",
    },
    {
      name: "responseTime",
      type: "number",
      label: "Response Time (hours)",
      placeholder: "Enter response time in hours",
      required: false,
      group: "Service Configuration",
      className: "col-span-1",
      min: 0,
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed from service request submission to initial response or acknowledgment. This is part of your service level agreement.",
    },
    {
      name: "resolutionTime",
      type: "number",
      label: "Resolution Time (hours)",
      placeholder: "Enter resolution time in hours",
      required: false,
      group: "Service Configuration",
      className: "col-span-2",
      min: 0,
      showHelperTooltip: true,
      tooltipText:
        "Maximum time allowed to complete the service from initial request. This includes all processing, approvals, and delivery steps.",
    },

    // Workflow Configuration Group
    {
      name: "fieldWorkRequired",
      type: "switch",
      label: "Field Work Required",
      group: "Workflow Configuration",
      className: "col-span-2",
      groupColorNeeded: false,
      showHelperTooltip: true,
      tooltipText:
        "Enable if this service requires physical field work or site visits. This affects scheduling, resource allocation, and completion tracking.",
    },
    {
      name: "customerNotification",
      type: "select",
      label: "Customer Notification",
      placeholder: "Select notification method",
      required: true,
      group: "Workflow Configuration",
      options: getTemplateOptions,
      className: "col-span-2",
      showHelperTooltip: true,
      tooltipText:
        "Choose how customers are notified about service status updates. Email provides detailed information, SMS offers quick alerts, and both ensures comprehensive communication.",
    },
    {
      name: "availability",
      type: "select",
      label: "Availability Settings",
      placeholder: "Select availability",
      required: true,
      group: "Workflow Configuration",
      options: getAvabilibiltyOptions,
      className: "col-span-2",
      showHelperTooltip: true,
      tooltipText:
        "Define when this service can be requested and processed. 24/7 means always available, Business Hours restricts to working hours, Emergency Only limits to urgent situations.",
    },
    {
      name: "requiresApproval",
      type: "switch",
      label: "Requires Approval",
      group: "Workflow Configuration",
      helperText: "Whether this reason requires manager approval",
      className: "col-span-2 mt-3",
    },
    {
      name: "visibleOnSelfServicePortal",
      type: "switch",
      label: "Visible on Self Service Portal",
      group: "Workflow Configuration",
      tooltipText:
        "When enabled, customers can see and request this service directly from the self service portal.",
      helperText:
        "Allow customers to view and submit this service request from the self service portal",
      className: "col-span-2 mt-3",
      showHelperTooltip: true,
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  useEffect(() => {
    form.reset(formData);
  }, [formData]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    switch (fieldName) {
      case "utility_service":
        console.log("value", value);
        setUtilityService(value);
        break;
      case "category":
        console.log("value", value);
        setCodeList(value);
        break;
      case "fieldWorkRequired":
        break;
    }
  };

  const handleSubmit = () => {
    if (formData.responseTime && formData.resolutionTime) {
      const responseTime = parseInt(formData.responseTime);
      const resolutionTime = parseInt(formData.resolutionTime);

      if (responseTime >= resolutionTime) {
        toast({
          title: "Validation Error",
          description: "Response time must be less than resolution time",
          variant: "destructive",
        });
        return;
      }
    }

    const operation = service ? "UPDATE" : "ADD";

    switch (operation) {
      case "UPDATE": {
        const updatePayload = {
          remote_utility_id: Number(remoteUtilityId),
          long_description: formData.description,
          product_code: formData.utility_service,
          support_request_type: formData.category,
          support_request_subtype: formData.subcategory,
          request_type: "Service",
          extra_data: {
            priority_level: parseInt(formData.priority),
            service_charge: formData.serviceFee,
            response_time: formData.responseTime,

            acknowlegment_time: formData.acknowledgmentTime,

            resolution_time: parseInt(formData.resolutionTime),
            field_force_required: formData.fieldWorkRequired,
            consumer_notification: parseInt(formData.customerNotification),
            availability: parseInt(formData.availability),
            requires_approval: formData.requiresApproval,
            is_visible_on_self_portal: formData.visibleOnSelfServicePortal ? 1 : 0,
          },
        };

        updateConfig.mutateAsync(updatePayload);
        onClose();
        handleReset();
        break;
      }
      case "ADD": {
        const payload = {
          remote_utility_id: Number(remoteUtilityId),
          name: formData.name,
          configuration_code: formData.code,
          long_description: formData.description,
          product_code: formData.utility_service,
          support_request_type: formData.category,
          support_request_subtype: formData.subcategory,
          request_type: "Service",
          extra_data: {
            priority_level: parseInt(formData.priority),
            service_charge: formData.serviceFee,

            acknowlegment_time: formData.acknowledgmentTime,

            response_time: formData.responseTime,
            resolution_time: formData.resolutionTime,
            field_force_required: formData.fieldWorkRequired,
            consumer_notification: parseInt(formData.customerNotification),
            availability: parseInt(formData.availability),
            requires_approval: formData.requiresApproval,
            is_visible_on_self_portal: formData.visibleOnSelfServicePortal ? 1 : 0,
          },
        };

        createServiceConfig.mutateAsync(payload);
        onClose();
        handleReset();
        break;
      }
    }
  };

  const handleReset = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            {service ? "Edit Service" : "Create New Service"}
            <ContextualHelp
              title="Service Configuration"
              content="Configure all aspects of a service including classification, SLA requirements, workflow settings, and availability options. Services are the building blocks of your customer service offerings."
            />
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 px-1">
          <div className="space-y-6 pr-4">
            {/* Prerequisites Check */}
            {(!categoriesExist || !subcategoriesExist) && (
              <Card className="border-orange-200 bg-orange-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-orange-800">
                        Missing Categories Alert
                      </h4>
                      <p className="text-sm text-orange-700 mt-1">
                        Some required categories or subcategories are missing.
                        Please create them first before configuring services.
                      </p>
                      <Button variant="outline" size="sm" className="mt-2">
                        Create Missing Categories
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Loading indicator for categories */}

            {/* Dynamic Form with Grid Layout */}
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: true,
                showSubmitButton: true,
                submitButtonText: service ? "Update Service" : "Create Service",
                resetOnSubmit: false,
                resetButtonText: "Cancel",
                spacing: "comfortable",
                classes: {
                  button: "px-6 py-2 rounded-md font-medium transition-colors",
                  submitButton:
                    "bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50",
                  resetButton:
                    "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
                  buttonContainer:
                    "flex justify-end gap-3 pt-6 border-t border-gray-200 mt-8",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white",
                  label: "block text-sm font-medium text-gray-700 mb-1",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-sm text-gray-500 mt-1 flex",
                  checkbox:
                    "h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500",
                },
              }}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
